import { isNil, isUndefined } from 'util/null'

type Predicate = (value: unknown, key: string) => boolean

export function omit<T>(object: object, skip?: Predicate): T {
  skip ??= isNil
  const copy: any = {}
  for (const [key, value] of Object.entries(object as {})) {
    if (!skip(value, key)) copy[key] = value
  }
  return copy
}

export function omitUndefined<T>(object: object): T {
  return omit(object, isUndefined)
}

export function isEmptyObject(value: object): boolean {
  return Object.values(value).length === 0
}

export function getKeys<T>(value: T | null | undefined): (keyof T)[] {
  if (!isObject(value)) return []
  return Object.keys(value) as (keyof T)[]
}

export function isObject(value: unknown): value is NonNullable<object> {
  return typeof value === 'object' && !Array.isArray(value) && value !== null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function updateObjectUnsafe(original: any, update: any): any {
  original = isObject(original) ? original : {}
  update = isObject(update) ? update : {}
  const copy = { ...original, ...update }
  for (const key of getKeys(copy)) {
    const prev = original[key]
    const next = update[key]
    if (!Object.hasOwn(update, key)) {
      copy[key] = prev
    } else if (next === undefined) {
      delete copy[key]
    } else if (next === null || Array.isArray(next)) {
      copy[key] = next
    } else if (isObject(next)) {
      copy[key] = updateObjectUnsafe(prev, next)
    } else {
      copy[key] = next
    }
  }
  return copy
}

export function updateObject<T extends object>(original: T, update: Partial<T>): T {
  return updateObjectUnsafe(original, update)
}

export function toUndefined<T>(value: T | null | undefined): T | undefined {
  if (value == null) return undefined
  if (value instanceof Array && !value.length) return undefined
  if (value instanceof Object && !Object.keys(value).length) return undefined
  return value
}
